import { Box, Grid, MenuItem, Select, Switch, Typography } from '@material-ui/core'
import TextField from '@material-ui/core/TextField'
import { useUser } from 'components/providers/UserProvider'
import { SetLocalStorageValueFunction } from 'lib/hooks'
import { useStockStore } from 'lib/hooks/useStockStore'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import * as React from 'react'
import { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  CARD_LANGUAGES,
  commentSuggestsUsEdition,
  CountryEdition,
  FINISH_TYPE_LABELS,
  IdGame,
  TAXATION_SCHEME,
  TAXATION_SCHEME_CONVERSIONS,
  Template,
} from 'shared'
import { CONDITIONS, EXTRA_ATTRIBUTES_VALUES_AND_IMAGES_MAP } from 'utils/constants'

interface ArticleTemplateProps {
  template: Template
  setTemplate: SetLocalStorageValueFunction<Template>
}

export const ArticleTemplate = ({ template, setTemplate }: ArticleTemplateProps): JSX.Element => {
  const { activeGame, user } = useUser()
  const { t } = useTranslation()

  const activeStockType = useStockStore((state) => state.activeStockType)
  const { enqueueSnackbar } = useSnackbar()

  const sanitizeInput = (fieldName: string, fieldValue: any): any => {
    if (fieldName === 'price') {
      const parsed = parseFloat(fieldValue)
      if (isNaN(parsed) || parsed <= 0) return 0.02
      return Math.round(parsed * 100) / 100
    }
    if (fieldName === 'buyPrice') {
      if (fieldValue === '' || fieldValue === null) return undefined
      const parsed = parseFloat(fieldValue)
      if (isNaN(parsed)) return undefined
      return Math.round(parsed * 100) / 100
    }
    if (fieldName === 'quantity') {
      if (fieldValue <= 0) return 1
      else return Number(fieldValue)
    }
    return fieldValue
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (e: React.ChangeEvent<any>): void => {
    const { name, type, checked, value } = e.target

    const sanitizedValue = type === 'checkbox' ? checked : sanitizeInput(name, value)

    const newTemplate: Template = {
      ...template,
      [name]: sanitizedValue,
    }

    if (name === 'quantity') {
      const qty = sanitizedValue as number

      if (template.taxationScheme != null) {
        if (template.taxationScheme === TAXATION_SCHEME.STANDARD) {
          newTemplate.standardTaxationQuantity = qty
          newTemplate.marginTaxationQuantity = 0
        } else if (template.taxationScheme === TAXATION_SCHEME.MARGIN) {
          newTemplate.standardTaxationQuantity = 0
          newTemplate.marginTaxationQuantity = qty
        }
      }
    }

    setTemplate(newTemplate)
  }

  const getFinishTypesForGame = (
    idGame: IdGame,
    stockType: 'singles' | 'sealed'
  ): FINISH_TYPE_LABELS[] => {
    const entry = EXTRA_ATTRIBUTES_VALUES_AND_IMAGES_MAP.find((e) => e.idGame === idGame)
    return entry?.finishType.filter((ft) => ft[stockType]).map((ft) => ft.label) ?? []
  }

  const isSingles = activeStockType === 'singles'
  const finishTypes = getFinishTypesForGame(activeGame.idGame, isSingles ? 'singles' : 'sealed')

  useEffect(() => {
    if (template.quantity === 0) {
      enqueueSnackbar(t('addArticles.templateQuantityError'), {
        variant: 'warning',
      })
    }
  }, [template.quantity, enqueueSnackbar, t])

  // Warn as soon as the comment starts saying the cards are American versions while the template is not set to
  // US Edition: every card listed with this template would then be unreachable for the Cardmarket sync.
  // Warned once per transition, otherwise every keystroke in the comment field would enqueue a snackbar.
  const usEditionCommentWarned = useRef(false)

  useEffect(() => {
    const needsWarning =
      commentSuggestsUsEdition(template.comment) && template.countryEdition !== CountryEdition.US

    if (needsWarning && !usEditionCommentWarned.current) {
      enqueueSnackbar(
        <span data-testid="us-edition-comment-warning">
          <Trans components={{ 1: <b></b> }} i18nKey="card.usEditionCommentWarning" />
        </span>,
        {
          variant: 'warning',
        }
      )
    }

    usEditionCommentWarned.current = needsWarning
  }, [template.comment, template.countryEdition, enqueueSnackbar, t])

  // Normalize finishType and apply default taxationScheme ONLY if missing
  useEffect(() => {
    let updated: Template = { ...template }
    let changed = false

    // Fix finishType based on game
    if (finishTypes.length > 0 && !finishTypes.includes(template.finishType)) {
      updated.finishType = FINISH_TYPE_LABELS.REGULAR
      changed = true
    } else if (finishTypes.length === 0 && template.finishType !== FINISH_TYPE_LABELS.REGULAR) {
      updated.finishType = FINISH_TYPE_LABELS.REGULAR
      changed = true
    }

    // Apply default taxation only if the field is truly empty (null/undefined)
    if (updated.taxationScheme == null && user?.defaultTaxationSchema) {
      const scheme =
        activeStockType === 'singles'
          ? user.defaultTaxationSchema.singles
          : user.defaultTaxationSchema.sealed

      updated = {
        ...updated,
        taxationScheme: scheme,
        standardTaxationQuantity: scheme === TAXATION_SCHEME.STANDARD ? template.quantity : 0,
        marginTaxationQuantity: scheme === TAXATION_SCHEME.MARGIN ? template.quantity : 0,
      }
      changed = true
    }

    if (changed && !_.isEqual(updated, template)) {
      setTemplate(updated)
    }
  }, [template, finishTypes, user, activeStockType, setTemplate])

  const ExtraAttributes = ({ finishTypes }: { finishTypes: string[] }): JSX.Element => {
    return (
      <>
        {finishTypes.length > 0 && (
          <>
            <Grid item xs={8} style={{ margin: 'auto' }}>
              <Typography>Finish type</Typography>
            </Grid>
            <Grid item xs={4}>
              <Select
                fullWidth
                value={template.finishType}
                data-testid="template-finishType"
                name="finishType"
                onChange={handleChange}
              >
                {finishTypes.map((finish) => (
                  <MenuItem key={finish} value={finish}>
                    {finish.replace(/^is/, '')}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </>
        )}

        {activeGame.extraAttr.map((attribute) => {
          if (attribute === 'isPlayset' || finishTypes.includes(attribute)) return null
          const entry = EXTRA_ATTRIBUTES_VALUES_AND_IMAGES_MAP.find(
            (e) => e.idGame === activeGame.idGame
          )
          const attrConfig = entry?.cardExtraAttribute.find((a) => a.attribute === attribute)
          if (attrConfig && !(isSingles ? attrConfig.singles : attrConfig.sealed)) return null
          return (
            <Grid container item xs={6} spacing={0} key={attribute}>
              <Grid item xs={4} className="d-flex align-items-center">
                <Typography>{t(`common.${attribute}`)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Switch
                  color="primary"
                  name={attribute}
                  checked={!!template[attribute as keyof typeof template]}
                  onChange={handleChange}
                ></Switch>
              </Grid>
            </Grid>
          )
        })}
      </>
    )
  }

  return (
    <>
      <Box data-testid="template-form" display="flex">
        <Grid container>
          <Grid item xs={8} style={{ margin: 'auto' }}>
            <Typography>{t('card.quantity')}</Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField
              value={template.quantity}
              onChange={handleChange}
              margin="dense"
              type="number"
              name="quantity"
              data-testid="template-quantity"
              fullWidth
            />
          </Grid>

          <Grid item xs={8} style={{ margin: 'auto' }}>
            <Typography>{t('card.sellPrice')}</Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField
              value={template.price}
              onChange={handleChange}
              margin="dense"
              type="number"
              name="price"
              data-testid="template-price"
              fullWidth
              className="my-2"
            />
          </Grid>

          {user.featureFlags?.taxation && (
            <>
              <Grid item xs={8} style={{ margin: 'auto' }}>
                <Typography>{t('taxation.buyPrice')}</Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  value={template.buyPrice}
                  onChange={handleChange}
                  name="buyPrice"
                  data-testid="template-buy-price"
                  margin="dense"
                  type="number"
                  fullWidth
                />
              </Grid>
            </>
          )}

          {user.featureFlags?.taxation && user.enableMarginTaxation && (
            <>
              <Grid item xs={8} style={{ margin: 'auto' }}>
                <Typography>{t('taxation.sellTaxationScheme')}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Select
                  disabled={true}
                  fullWidth
                  value={template.taxationScheme}
                  name="taxationScheme"
                  data-testid="template-taxation-schema"
                  onChange={(e) => {
                    const scheme = Number(e.target.value) as TAXATION_SCHEME
                    const newInputs = { ...template }

                    // reset both fields
                    newInputs.standardTaxationQuantity = 0
                    newInputs.marginTaxationQuantity = 0

                    // set the chosen one
                    if (scheme === TAXATION_SCHEME.STANDARD) {
                      newInputs.standardTaxationQuantity = template.quantity
                    } else {
                      newInputs.marginTaxationQuantity = template.quantity
                    }

                    newInputs.taxationScheme = scheme // only UI

                    setTemplate(newInputs)
                  }}
                  className="my-2"
                >
                  {TAXATION_SCHEME_CONVERSIONS.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </>
          )}

          {activeStockType === 'singles' && (
            <>
              <Grid item xs={8} style={{ margin: 'auto' }}>
                <Typography>{t('card.condition')}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Select
                  fullWidth
                  value={template.condition}
                  name="condition"
                  data-testid="template-condition"
                  onChange={handleChange}
                >
                  {CONDITIONS.map((option) => (
                    <MenuItem key={option.name} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </>
          )}

          <Grid item xs={8} style={{ margin: 'auto' }}>
            <Typography>{t('card.language')}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Select
              fullWidth
              value={template.idLanguage}
              name="idLanguage"
              data-testid="template-idLanguage"
              onChange={handleChange}
              className="my-2"
            >
              {CARD_LANGUAGES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <ExtraAttributes finishTypes={finishTypes} />

          <Grid container>
            <Grid item xs={12}>
              <TextField
                value={template.location}
                onChange={handleChange}
                name="location"
                data-testid="template-location"
                margin="dense"
                helperText={t('card.location')}
                type="text"
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <TextField
                value={template.comment}
                onChange={handleChange}
                name="comment"
                data-testid="template-comment"
                margin="dense"
                helperText={t('card.comment')}
                type="text"
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
